import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'
import Icon from '@/components/ui/icon'
import { useFetchSignatureByIdUser } from '@/features/signatures/services'
import { useCurrentUser } from '@/features/auth/state/auth'

import { useTheme } from '@/components/theme-provider'
import SignatureDialog from '@/features/signatures/components/signature-dialog'
import { useEffect, useState } from 'react'
import { useToast } from '@/lib/hooks/use-toast'
import useSignOut from '@/features/auth/services/useSignOut'
import { formatNome } from '@/utils/format'
import UserDetailsModal from '@/features/users/components/user-details-modal'

function extractAvatarFallback(userName: string) {
  if (userName.split(' ').length > 1) {
    const names = userName.split(' ')
    return `${names[0][0]}${names[1][0]}`.toUpperCase()
  }

  return `${userName[0]}${userName[1]}`.toUpperCase()
}

export function NavUser() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { isMobile } = useSidebar()
  const user = useCurrentUser()
  const { signOut } = useSignOut()
  const { theme, setTheme } = useTheme()
  const { toast } = useToast()

  const [showEditDialog, setShowEditDialog] = useState(false)

  const handleCloseDialog = () => {
    setShowEditDialog(false)
  }
  const { data: signatureStatus } = useFetchSignatureByIdUser(user?.id)

  useEffect(() => {
    if (signatureStatus?.data?.isModifica) {
      setIsDialogOpen(true)
    }
  }, [signatureStatus, setIsDialogOpen])

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Erro ao sair:', error)
    }
  }

  if (!user) return null
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="
            group/button
            h-14 py-3
           
            data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground
          "
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className=" bg-primary text-primary-foreground">
                  {extractAvatarFallback(user.name)}
                </AvatarFallback>
              </Avatar>

              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{formatNome(user.name)}</span>
                <span className="truncate text-xs">{user.email}</span>

                {/* Quando o dropdown estiver aberto (clique no sidebar), fica text-primary */}
                <span
                  className="
                truncate text-xs
                text-primary
                transition-colors
                group-data-[state=open]/button:text-primary
             

              "
                >
                  {formatNome(user.typeCargo)}
                  {user.typeUser === 'ADMIN' ? ' - Admin' : ''}
                </span>
              </div>
              <Icon name="ChevronsUpDown" className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{extractAvatarFallback(user.name)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{formatNome(user.name)}</span>
                  <span className="truncate text-xs">{user.email}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {formatNome(user.typeCargo)}
                    {user.typeUser === 'ADMIN' ? ' - Admin' : ''}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={e => {
                e.preventDefault()
                if (signatureStatus?.data?.isModifica) {
                  setIsDialogOpen(true)
                } else {
                  toast({
                    variant: 'destructive',
                    description: 'Assinatura indisponível, você não pode assinar no momento.'
                  })
                }
              }}
            >
              {signatureStatus?.data?.isModifica ? (
                <span className="cursor-pointer">Assinatura</span>
              ) : (
                <span className="text-gray-500 cursor-not-allowed">Assinatura</span>
              )}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={e => {
                e.preventDefault()
                setIsDialogOpen(false) // garante só 1 diálogo
                requestAnimationFrame(() => setShowEditDialog(true))
              }}
            >
              Perfil
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={ev => {
                  ev.preventDefault()
                  // if (theme === 'dark') {
                    setTheme('light')
                  // } else {
                  //   setTheme('dark')
                  // }
                }}
              >
                <Icon name={theme === 'dark' ? 'Moon' : 'Sun'} className="mr-2" />
                Mudar para tema {theme === 'dark' ? 'claro' : 'escuro'}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <Icon name="LogOut" className="mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <SignatureDialog user={user} open={isDialogOpen} onOpenChange={setIsDialogOpen} />
        <UserDetailsModal isOpen={showEditDialog} onClose={handleCloseDialog} user={user || null} />
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
